/**
 * CcqSection — remises mensuelles CCQ publiées par le gestionnaire (Batch #14.299). Même présentation que les DAS :
 * sélecteur année / mois → montants publiés (ou en attente) + PDF.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, HardHat, Loader2 } from "lucide-react";
import { API, portalHeaders, SectionCard, inputCls, YEARS, MONTHS_LONG, fmtDate, portalDownload, err } from "./_shared";
import { fmtCAD as fmt } from "@/lib/format";

export default function CcqSection({ primaryColor }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/portal/paie/ccq?year=${year}`, { headers: portalHeaders() }); setData(r.data); }
    catch (e) { err(e, "Erreur chargement des remises CCQ"); setData({ items: [] }); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const entry = (data?.items || []).find((it) => it.month === month) || null;
  const published = new Set((data?.items || []).map((it) => it.month));
  const fields = entry?.fields || [];

  return (
    <SectionCard title="Remises CCQ (rapport mensuel)" icon={HardHat} primaryColor={primaryColor} testId="paie-portal-ccq">
      <p className="text-xs text-gray-500 -mt-2 mb-3">Sélectionnez le mois. Les montants à remettre à la Commission de la construction du Québec apparaissent lorsque votre gestionnaire les a préparés.</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Année</label>
          <select className={inputCls} value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="paie-portal-ccq-year">{YEARS.map((y) => <option key={y} value={y}>{y}</option>)}</select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Mois</label>
          <select className={inputCls} value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="paie-portal-ccq-month">
            {MONTHS_LONG.map((m, i) => <option key={m} value={i + 1}>{m}{published.has(i + 1) ? " ●" : ""}</option>)}
          </select>
        </div>
      </div>
      {!data ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-300 mx-auto" /></div> : (
        <div className="mt-4 border rounded-2xl overflow-hidden" data-testid={`paie-portal-ccq-card-${month}`}>
          <div className="px-4 py-3 text-white flex items-center justify-between gap-2 flex-wrap" style={{ backgroundColor: primaryColor }}>
            <div><div className="text-sm font-semibold">Commission de la construction du Québec</div><div className="text-xs opacity-80">{MONTHS_LONG[month - 1]} {year}</div></div>
            {entry ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/20" data-testid="paie-portal-ccq-published">Préparé le {String(entry.at || "").slice(0, 10)}</span>
              : <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/20" data-testid="paie-portal-ccq-empty">En attente de votre gestionnaire</span>}
          </div>
          <div className="divide-y">
            {entry && (
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm bg-slate-50" data-testid="paie-portal-ccq-hours">
                <span className="text-gray-700">Heures rapportées · salaires cotisables</span><span className="font-mono text-gray-900">{entry.totals?.hours} h · {fmt(entry.totals?.wages)}</span>
              </div>
            )}
            {(entry ? fields : [{ code: "vacation", label: "Indemnité de congés (13 %)" }, { code: "levy", label: "Prélèvement CCQ" }, { code: "benefits", label: "Avantages sociaux" }, { code: "union", label: "Cotisations syndicales" }]).map((fld) => (
              <div key={fld.code} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm" data-testid={`paie-portal-ccq-field-${fld.code}`}>
                <span className="text-gray-700">{fld.label}</span>
                <span className={`font-mono min-w-[110px] text-right px-2 py-1 rounded-lg border ${entry ? "bg-white text-gray-900" : "bg-gray-50 text-transparent border-dashed"}`}>{entry ? fmt(entry.declared?.[fld.code]) : "0,00 $"}</span>
              </div>
            ))}
            <div className="flex items-center justify-between gap-3 px-4 py-3 text-sm font-bold bg-gray-50" data-testid="paie-portal-ccq-total">
              <span>Total à remettre</span>
              <span className={`font-mono min-w-[110px] text-right px-2 py-1 rounded-lg border ${entry ? "bg-white" : "text-transparent border-dashed"}`}>{entry ? fmt(entry.total) : "0,00 $"}</span>
            </div>
            {entry && Math.abs(entry.hidden_amount || 0) >= 0.005 && <div className="px-4 py-2 text-[11px] text-gray-500 italic" data-testid="paie-portal-ccq-hidden-note">Le total inclut {fmt(entry.hidden_amount)} d'éléments non détaillés ci-dessus.</div>}
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
              <span className="text-gray-700">Échéance (rapport et paiement)</span>
              <span className={`font-medium ${entry?.due_date ? "text-gray-900" : "text-gray-300"}`} data-testid="paie-portal-ccq-due">{entry?.due_date ? fmtDate(entry.due_date) : "—"}</span>
            </div>
            {entry?.note && <div className="px-4 py-2.5 text-xs text-gray-700 bg-amber-50" data-testid="paie-portal-ccq-note">{entry.note}</div>}
          </div>
          {entry && (
            <div className="p-3 border-t bg-white">
              <button type="button" disabled={busy} onClick={() => portalDownload(`/portal/paie/ccq/${year}/${month}/pdf`, `Remise_CCQ_${year}-${String(month).padStart(2, "0")}.pdf`, setBusy)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: primaryColor }} data-testid="paie-portal-ccq-pdf">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Télécharger en PDF
              </button>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}
